import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import { EmptyState, StatusBadge, TableRowActions, SortableTh, TableFilterBar, TablePagination } from '@/shared/components'
import { usePeriodes, useDeletePeriode } from '../hooks/use-periodes'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { useTableSort } from '@/shared/hooks/use-table-sort'
import { usePagination } from '@/shared/hooks/use-pagination'
import { cn } from '@/lib/utils'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'
import type { Periode } from '@/shared/types/admin'

const TYPE_LABELS: Record<string, string> = {
  ECOLE: 'École',
  VACANCES: 'Vacances',
  RELAX: 'Relax',
  SEMAINE_A: 'Semaine A',
  SEMAINE_B: 'Semaine B',
}

function PeriodeRow({ periode, onDelete }: { periode: Periode; onDelete: () => void }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [expanded, setExpanded] = useState(false)
  const dateCount = periode.datesPeriodes?.length || 0

  return (
    <>
      <tr
        className={cn(
          'cursor-pointer transition-colors hover:bg-neutral-50',
          !periode.actif && 'opacity-50',
        )}
        onClick={() => dateCount > 0 && setExpanded(!expanded)}
      >
        <td className="w-8 px-2 py-3 text-center">
          {dateCount > 0 && (
            <ChevronRight
              className={cn(
                'h-4 w-4 text-neutral-400 transition-transform',
                expanded && 'rotate-90',
              )}
            />
          )}
        </td>
        <td className="px-4 py-3 text-sm font-medium text-neutral-900">
          {periode.titre}
        </td>
        <td className="px-4 py-3">
          {periode.typePeriode && (
            <StatusBadge label={TYPE_LABELS[periode.typePeriode] || periode.typePeriode} variant="info" />
          )}
        </td>
        <td className="px-4 py-3 text-sm text-neutral-500">
          {dateCount} {dateCount === 1 ? 'date' : 'dates'}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1.5">
            {periode.actif ? (
              <StatusBadge label={t('administration.sections.periods.table.active', 'Actif')} variant="active" />
            ) : (
              <StatusBadge label={t('administration.sections.periods.table.inactive', 'Inactif')} variant="inactive" />
            )}
            {periode.estSysteme && (
              <StatusBadge label={t('administration.sections.periods.table.system', 'Système')} variant="system" />
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
          {(!periode.estSysteme || isAdmin) && (
            <TableRowActions
              editTo={`/administration/periodes/${periode.id}`}
              onDelete={onDelete}
            />
          )}
        </td>
      </tr>
      {expanded && dateCount > 0 && (
        <tr>
          <td colSpan={6} className="bg-neutral-50/70 px-4 py-3">
            <div className="ml-6 space-y-1.5">
              {periode.datesPeriodes.map((dp, i) => (
                <div
                  key={dp.id || i}
                  className="flex items-center gap-4 rounded-lg border border-neutral-100 bg-white px-3 py-2 text-xs"
                >
                  <span className="w-44 font-medium text-neutral-700">{dp.libelle}</span>
                  <span className="text-neutral-500">
                    {dp.dateDebut} → {dp.dateFin}
                  </span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

export function PeriodeList() {
  const { t } = useTranslation()
  const { data: periodes, isLoading } = usePeriodes()
  const deletePeriode = useDeletePeriode()
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')

  const filtered = useMemo(() => {
    if (!periodes) return []
    let result = periodes
    if (statusFilter === 'actif') result = result.filter((p) => p.actif)
    if (statusFilter === 'inactif') result = result.filter((p) => !p.actif)
    if (typeFilter !== 'all') result = result.filter((p) => p.typePeriode === typeFilter)
    return result
  }, [periodes, statusFilter, typeFilter])

  const { sortedData, sortKey, sortDirection, handleSort } = useTableSort(filtered, 'titre')
  const { paginatedData, currentPage, totalPages, totalItems, pageSize, setCurrentPage } = usePagination(sortedData)

  const [deleteDialog, setDeleteDialog] = useState<{ id: number; label: string } | null>(null)

  const handleDelete = (id: number, titre: string) => {
    setDeleteDialog({ id, label: titre })
  }

  const handleDeleteConfirm = () => {
    if (deleteDialog) {
      deletePeriode.mutate(deleteDialog.id)
      setDeleteDialog(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!periodes || periodes.length === 0) {
    return <EmptyState message={t('administration.sections.periods.noPeriods', 'Aucune période disponible. Créez votre première période !')} />
  }

  const counts = {
    all: periodes.length,
    actif: periodes.filter((p) => p.actif).length,
    inactif: periodes.filter((p) => !p.actif).length,
  }

  const typeCounts: Record<string, number> = {}
  for (const p of periodes) {
    if (p.typePeriode) {
      typeCounts[p.typePeriode] = (typeCounts[p.typePeriode] || 0) + 1
    }
  }

  const typeOptions = [
    { value: 'all', label: 'Tous', count: periodes.length },
    ...Object.entries(TYPE_LABELS)
      .filter(([key]) => typeCounts[key])
      .map(([key, label]) => ({ value: key, label, count: typeCounts[key] })),
  ]

  return (
    <>
      <TableFilterBar
        filters={[
          {
            key: 'status',
            label: 'Statut',
            value: statusFilter,
            options: [
              { value: 'all', label: 'Tous', count: counts.all },
              { value: 'actif', label: 'Actifs', count: counts.actif },
              { value: 'inactif', label: 'Inactifs', count: counts.inactif },
            ],
          },
          {
            key: 'type',
            label: 'Type',
            value: typeFilter,
            options: typeOptions,
          },
        ]}
        onFilterChange={(key, value) => {
          if (key === 'status') setStatusFilter(value)
          if (key === 'type') setTypeFilter(value)
        }}
      />
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-200 bg-gradient-to-r from-primary/5 to-transparent">
              <th className="w-8 px-2 py-3" />
              <SortableTh label="Titre" sortKey="titre" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Type" sortKey="typePeriode" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Dates" sortKey="datesPeriodes" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Statut" sortKey="actif" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-neutral-400" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {paginatedData.map((periode) => (
              <PeriodeRow
                key={periode.id}
                periode={periode}
                onDelete={() => handleDelete(periode.id, periode.titre)}
              />
            ))}
          </tbody>
        </table>
        <TablePagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize} onPageChange={setCurrentPage} />
      </div>

      <ConfirmDialog
        open={!!deleteDialog}
        variant="danger"
        title={`Supprimer la période "${deleteDialog?.label}" ?`}
        description="Cette action est irréversible."
        confirmLabel="Supprimer"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog(null)}
      />
    </>
  )
}
